import { ChevronDown, Filter, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Select } from '@/components/ui/Select';
import { cn } from '@/lib/utils';
import type { ActivityListParams } from '@/types/activity';
import {
  ACTIVITY_STATUS_FILTER_OPTIONS,
  ACTIVITY_TYPE_FILTER_OPTIONS,
} from '../../activities/constants';
import type { TabKey } from './activityTheme';

interface ActivityFilterBarProps {
  activeTab: TabKey;
  onSelectTab: (tab: TabKey) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  filters: ActivityListParams;
  onFilterChange: (filters: ActivityListParams) => void;
  isFilterPanelOpen: boolean;
  onToggleFilterPanel: () => void;
  tabCounts?: Record<TabKey, number>;
}

export function ActivityFilterBar({
  activeTab,
  onSelectTab,
  searchQuery,
  onSearchChange,
  filters,
  onFilterChange,
  isFilterPanelOpen,
  onToggleFilterPanel,
  tabCounts,
}: ActivityFilterBarProps) {
  // The class is chosen in the header and always set, so it is not a filter here.
  const hasExtraFilters = Boolean(filters.activityType || filters.dueDate || filters.status);

  return (
    <>
      {/* Tabs & Search Filter Header */}
      <div
        id="activity-filter-bar"
        className="card bg-surface border border-line rounded-[16px] px-4 py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 shadow-2xs scroll-mt-24"
      >
        {/* Status Tabs */}
        <div className="flex items-center gap-4 sm:gap-6 overflow-x-auto no-scrollbar border-b sm:border-b-0 border-line pb-2 sm:pb-0 scroll-smooth">
          {[
            { key: 'all' as TabKey, label: 'All Activities' },
            { key: 'upcoming' as TabKey, label: 'Upcoming' },
            { key: 'ongoing' as TabKey, label: 'Ongoing' },
            { key: 'completed' as TabKey, label: 'Completed' },
            { key: 'draft' as TabKey, label: 'Drafts' },
            { key: 'cancelled' as TabKey, label: 'Cancelled' },
          ].map((tab) => {
            const isActive = activeTab === tab.key;
            const count = tabCounts?.[tab.key] ?? 0;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={(e) => {
                  onSelectTab(tab.key);
                  e.currentTarget.scrollIntoView({
                    behavior: 'smooth',
                    inline: 'center',
                    block: 'nearest',
                  });
                }}
                className={cn(
                  'flex items-center gap-1.5 text-[13px] font-bold transition-all relative py-1 cursor-pointer whitespace-nowrap',
                  isActive ? 'text-indigo-400' : 'text-ink-3 hover:text-ink',
                )}
              >
                <span>{tab.label}</span>
                <span
                  className={cn(
                    'px-1.5 py-0.5 rounded-full text-[11px] font-bold transition-colors leading-none',
                    isActive
                      ? 'bg-indigo-500/20 text-indigo-400'
                      : 'bg-surface-2 text-ink-3 border border-line/60',
                  )}
                >
                  {count}
                </span>
                {isActive && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500 rounded-full" />
                )}
              </button>
            );
          })}
        </div>

        {/* Filter & Search Input */}
        <div className="flex items-center gap-2 sm:gap-2.5 w-full sm:w-auto">
          {/* Filter Button */}
          <div className="relative shrink-0">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Filter className="h-3.5 w-3.5" />}
              rightIcon={<ChevronDown className="h-3 w-3 ml-1" />}
              className="h-auto py-2 px-3 text-[12px] font-semibold"
              onClick={onToggleFilterPanel}
            >
              Filter
            </Button>
          </div>

          {/* Search input */}
          <div className="relative flex-1 sm:w-52">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-3" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search activities..."
              className="h-9 w-full pl-8 pr-2.5 text-sm bg-surface-2 border border-line rounded-field text-ink placeholder:text-base placeholder:text-ink-3 focus:outline-none focus:border-indigo-500 transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Expandable Filter Panel */}
      {isFilterPanelOpen && (
        <div className="card bg-surface border border-line rounded-[16px] p-4 flex flex-wrap items-end gap-3 shadow-2xs animate-in fade-in duration-150">
          <div className="w-full sm:w-48">
            <Select
              label="Kind of activity"
              labelClassName="!text-slate-600 dark:!text-slate-400 !text-[12px] font-bold"
              className="text-sm font-medium"
              options={ACTIVITY_TYPE_FILTER_OPTIONS}
              value={filters.activityType ?? ''}
              onChange={(e) =>
                onFilterChange({
                  ...filters,
                  activityType: e.target.value as ActivityListParams['activityType'],
                })
              }
            />
          </div>

          <div className="w-full sm:w-44">
            <Select
              label="Status"
              labelClassName="!text-slate-600 dark:!text-slate-400 !text-[12px] font-bold"
              className="text-sm font-medium"
              options={ACTIVITY_STATUS_FILTER_OPTIONS}
              value={filters.status ?? ''}
              onChange={(e) =>
                onFilterChange({
                  ...filters,
                  status: e.target.value as ActivityListParams['status'],
                })
              }
            />
          </div>

          <div className="w-full sm:w-44">
            <DatePicker
              label="Due date"
              labelClassName="!text-slate-600 dark:!text-slate-400 !text-[12px] font-bold"
              className="text-sm font-medium"
              // isClearable
              placeholder="Select date"
              value={filters.dueDate ?? ''}
              onChange={(e) => onFilterChange({ ...filters, dueDate: e.target.value })}
            />
          </div>

          <Button
            variant={hasExtraFilters ? 'dangerSoft' : 'secondary'}
            size="sm"
            disabled={!hasExtraFilters}
            leftIcon={
              <X
                className={cn(
                  'h-3.5 w-3.5 transition-colors',
                  hasExtraFilters ? 'text-rose-600 dark:text-rose-400' : 'text-ink-3 opacity-60',
                )}
              />
            }
            className={cn(
              'h-9 px-2.5 text-xs font-semibold transition-colors',
              hasExtraFilters
                ? 'border-rose-500/40 bg-rose-500/10 text-rose-600 hover:border-rose-500 hover:bg-rose-500/20 hover:text-rose-700 dark:border-rose-500/40 dark:text-rose-400 dark:hover:bg-rose-500/20'
                : 'opacity-50 cursor-not-allowed',
            )}
            onClick={() =>
              onFilterChange({
                ...filters,
                activityType: '',
                dueDate: '',
                status: '',
              })
            }
          >
            Clear all
          </Button>
        </div>
      )}
    </>
  );
}
